// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { css } from '@emotion/css';
import { TextField, MenuItem, Divider, Theme, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { ReportingRange } from '../types';
import { useReporting } from '../providers/ReportingProvider';

// COMPONENT ---------------------------------------------------------------- //

const ReportingControls = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { range, start, end, disablePickers, onRangeChange, onDateChange } = useReporting();

  return (
    <div className={cls.controls}>
      <TextField
        select
        fullWidth
        label='Date Range'
        placeholder='Select a date range...'
        variant='outlined'
        size='small'
        value={range}
        onChange={e => onRangeChange(e.target.value as ReportingRange)}
      >
        <MenuItem value='last7'>
          <span className={cls.rangeText}>Last 7 Days</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(6, 'day').format('MM/DD/YYYY')} - {dayjs().format('MM/DD/YYYY')}
          </span>
        </MenuItem>
        <MenuItem value='last30'>
          <span className={cls.rangeText}>Last 30 Days</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(29, 'day').format('MM/DD/YYYY')} - {dayjs().format('MM/DD/YYYY')}
          </span>
        </MenuItem>
        <MenuItem value='last90'>
          <span className={cls.rangeText}>Last 90 Days</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(89, 'day').format('MM/DD/YYYY')} - {dayjs().format('MM/DD/YYYY')}
          </span>
        </MenuItem>

        <Divider />

        <MenuItem value='rolling30'>
          <span className={cls.rangeText}>Rolling 30 Days</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(15, 'day').format('MM/DD/YYYY')} - {dayjs().add(15, 'day').format('MM/DD/YYYY')}
          </span>
        </MenuItem>

        <Divider />

        <MenuItem value='thisMonth'>
          <span className={cls.rangeText}>This Month</span>
          <span className={cls.rangeSubText}>
            {dayjs().startOf('month').format('MM/DD/YYYY')} - {dayjs().endOf('month').format('MM/DD/YYYY')}
          </span>
        </MenuItem>
        <MenuItem value='lastMonth'>
          <span className={cls.rangeText}>Last Month</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
            {dayjs().subtract(1, 'month').endOf('month').format('MM/DD/YYYY')}
          </span>
        </MenuItem>

        <Divider />

        <MenuItem value='custom'>Custom</MenuItem>
      </TextField>

      <DatePicker
        disabled={disablePickers}
        maxDate={dayjs(end)}
        label='Start Date'
        orientation='portrait'
        value={dayjs(start)}
        onChange={date => onDateChange('start')(date?.toString() || '')}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            variant: 'outlined',
          },
        }}
      />

      <DatePicker
        disabled={disablePickers}
        minDate={dayjs(start)}
        label='End Date'
        orientation='portrait'
        value={dayjs(end)}
        onChange={date => onDateChange('end')(date?.toString() || '')}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            variant: 'outlined',
          },
        }}
      />
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  return {
    controls: css`
      display: flex;
      gap: ${theme?.spacing(1.5)};
      max-width: 720px;
      ${theme?.breakpoints?.down('sm')} {
        flex-direction: column;
        max-width: 100%;
      }
    `,
    rangeText: css`
      flex: 1;
    `,
    rangeSubText: css`
      margin-left: ${theme?.spacing(2)};
      font-size: 12px;
      color: ${theme?.palette?.text?.disabled};
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default ReportingControls;
